import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
const ROUTES = { salesDocs: '/sales-docs' };

function DetailPageLayout({
  title,
  subtitle,
  badge,
  onBack,
  backLabel,
  children,
}: {
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  breadcrumbs?: { label: string; to?: string }[];
  onBack?: () => void;
  backLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center gap-4">
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            ← {backLabel || 'Back'}
          </Button>
        )}
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-on-surface">{title}</h1>
            {badge}
          </div>
          {subtitle && <p className="text-sm text-on-surface-variant mt-1">{subtitle}</p>}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}
import { Spinner } from '@/shared/components/ui/Spinner';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useUser } from '@/shared/hooks/useAuth';
import { useSalesDoc, useSalesDocMutations } from '../hooks/useSalesDocs';
import { SalesDocDrawerContent } from '../components/SalesDocDrawer';
import { SalesDocFormModal } from '../components/SalesDocFormModal';
import { RecordPaymentModal } from '../components/RecordPaymentModal';
import type { CreateSalesDocPayload } from '../types';
import { cn, cardClassName } from '@jira-clone/ui';

export function SalesDocDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useUser();
  const role = user?.role ?? '';
  const dept = user?.department ?? '';

  const { data: doc, isLoading } = useSalesDoc(id);
  const { updateDoc, deleteDoc } = useSalesDocMutations();

  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!doc || !id) {
    return (
      <DetailPageLayout
        title="Document Not Found"
        breadcrumbs={[{ label: 'Sales Docs', to: ROUTES.salesDocs }, { label: 'Not Found' }]}
        onBack={() => navigate(ROUTES.salesDocs)}
        backLabel="Back to Sales Docs"
      >
        <div className={cn(cardClassName(), 'p-8 text-center')}>
          <p className="text-on-surface-variant">The requested sales document could not be found.</p>
          <Button variant="outline" pill className="mt-4" onClick={() => navigate(ROUTES.salesDocs)}>
            Return to Sales Docs
          </Button>
        </div>
      </DetailPageLayout>
    );
  }

  const canManage =
    role === 'superadmin' ||
    ['sales', 'management', 'accounts', 'finance'].includes(dept) ||
    doc.uploaded_by === user?.id;

  const canRecordPayment =
    (role === 'superadmin' || ['accounts', 'finance'].includes(dept)) &&
    doc.type === 'invoice' &&
    doc.status !== 'paid';

  const handleUpdate = (docId: string, payload: Partial<CreateSalesDocPayload>) => {
    updateDoc.mutate(
      { id: docId, payload },
      {
        onSuccess: () => setEditOpen(false),
      }
    );
  };

  const handleDelete = () => {
    deleteDoc.mutate(doc.id, {
      onSuccess: () => {
        setConfirmDelete(false);
        navigate(ROUTES.salesDocs);
      },
    });
  };

  const typeLabel = (doc.type || 'other').split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <DetailPageLayout
      title={doc.title}
      subtitle={doc.reference_number ? `Ref: ${doc.reference_number}` : undefined}
      badge={
        <div className="flex items-center gap-2">
          <Badge tone={doc.type === 'proposal' ? 'primary' : doc.type === 'quotation' ? 'success' : doc.type === 'invoice' ? 'warning' : 'neutral'}>
            {typeLabel}
          </Badge>
          {doc.status && (
            <Badge tone={doc.status === 'paid' ? 'success' : doc.status === 'accepted' ? 'success' : 'neutral'}>
              {doc.status.toUpperCase()}
            </Badge>
          )}
        </div>
      }
      breadcrumbs={[{ label: 'Sales Docs', to: ROUTES.salesDocs }, { label: doc.title }]}
      onBack={() => navigate(ROUTES.salesDocs)}
      backLabel="Back to Sales Docs"
    >
      <SalesDocDrawerContent
        doc={doc}
        canManage={canManage}
        canRecordPayment={canRecordPayment}
        deleting={deleteDoc.isPending}
        isPage
        onClose={() => navigate(ROUTES.salesDocs)}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setConfirmDelete(true)}
        onRecordPayment={() => setRecordPaymentOpen(true)}
      />

      <SalesDocFormModal
        open={editOpen}
        doc={doc}
        loading={updateDoc.isPending}
        onClose={() => setEditOpen(false)}
        onCreate={() => {}}
        onUpdate={handleUpdate}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete Document"
        message={`Are you sure you want to delete "${doc.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteDoc.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <RecordPaymentModal
        open={recordPaymentOpen}
        invoice={doc}
        onClose={() => setRecordPaymentOpen(false)}
      />
    </DetailPageLayout>
  );
}

export default SalesDocDetailPage;
